import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/List/doublyInsert.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";


const DoublyLinkedListInsertionGame = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [nodes, setNodes] = useState([]);
    const [headId, setHeadId] = useState(null);
    const [tailId, setTailId] = useState(null);
    const [pendingNodes, setPendingNodes] = useState([]);
    const [lives, setLives] = useState(3);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [actionMode, setActionMode] = useState(null);
    const [reconnectFrom, setReconnectFrom] = useState(null);
    const [reconnectDirection, setReconnectDirection] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [insertMode, setInsertMode] = useState(null);
    const [insertBetweenNodes, setInsertBetweenNodes] = useState({ before: null, after: null });
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    const [removedEdges, setRemovedEdges] = useState(new Set());

    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const LEVELS = [
        {
            id: 1,
            title: selLanguage
                ? "Επίπεδο 1: Εισαγωγή στην Αρχή"
                : "Level 1: Insert at Beginning",
            description: selLanguage
                ? "Εισήγαγε νέο κόμβο με τιμή 5 στην αρχή της λίστας. Για να συνδέσεις έναν κόμβο πρέπει να επιλέξεις την λειτουργία και να διαλέξεις τους 2 κόμβους που θες να συνδέσεις. Σε περίπτωση που υπάρχουν >1 κόμβος προς εισαγωγή μπαίνουν έανς ένας με την σειρά ακολουθώντας την διαδικασία για καθένα ξεχωριστά."
                : "Insert a new node with value 5 at the BEGINNING of the list. To connect a node, you must select the action and choose the 2 nodes you want to connect. If there are >1 nodes to insert, they are inserted one by one in order following the process for each separately.",
            initialList: [
                { id: "B", value: "15", next: "C", prev: null },
                { id: "C", value: "25", next: "D", prev: "B" },
                { id: "D", value: "35", next: null, prev: "C" }
            ],
            headId: "B",
            tailId: "D",
            newNode: { id: "A", value: "5" },
            expectedHead: "A",
            expectedTail: "D",
            expectedList: [
                { id: "A", value: "5", next: "B", prev: null },
                { id: "B", value: "15", next: "C", prev: "A" },
                { id: "C", value: "25", next: "D", prev: "B" },
                { id: "D", value: "35", next: null, prev: "C" }
            ]
        },
        {
            id: 2,
            title: selLanguage
                ? "Επίπεδο 2: Εισαγωγή στο Τέλος"
                : "Level 2: Insert at End",
            description: selLanguage
                ? "Εισήγαγε νέο κόμβο με τιμή 100 στο τέλος της λίστας."
                : "Insert a new node with value 100 at the END of the list.",
            initialList: [
                { id: "P", value: "10", next: "Q", prev: null },
                { id: "Q", value: "20", next: "R", prev: "P" },
                { id: "R", value: "30", next: null, prev: "Q" }
            ],
            headId: "P",
            tailId: "R",
            newNode: { id: "S", value: "100" },
            expectedHead: "P",
            expectedTail: "S",
            expectedList: [
                { id: "P", value: "10", next: "Q", prev: null },
                { id: "Q", value: "20", next: "R", prev: "P" },
                { id: "R", value: "30", next: "S", prev: "Q" },
                { id: "S", value: "100", next: null, prev: "R" }
            ]
        },
        {
            id: 3,
            title: selLanguage
                ? "Επίπεδο 3: Εισαγωγή Πριν από Συγκεκριμένο Κόμβο"
                : "Level 3: Insert Before Specific Node",
            description: selLanguage
                ? "Εισήγαγε κόμβο Μ με τιμή 45 πρίν τον κόμβο Y."
                : "Insert node M with value 45 BEFORE node Y.",
            initialList: [
                { id: "W", value: "30", next: "X", prev: null },
                { id: "X", value: "40", next: "Y", prev: "W" },
                { id: "Y", value: "50", next: "Z", prev: "X" },
                { id: "Z", value: "60", next: null, prev: "Y" }
            ],
            headId: "W",
            tailId: "Z",
            newNode: { id: "M", value: "45" },
            insertBefore: "Y",
            expectedHead: "W",
            expectedTail: "Z",
            expectedList: [
                { id: "W", value: "30", next: "X", prev: null },
                { id: "X", value: "40", next: "M", prev: "W" },
                { id: "M", value: "45", next: "Y", prev: "X" },
                { id: "Y", value: "50", next: "Z", prev: "M" },
                { id: "Z", value: "60", next: null, prev: "Y" }
            ]
        },

        {
            id: 4,
            title: selLanguage
                ? "Επίπεδο 5: Διπλή Εισαγωγή - Master Challenge!"
                : "Level 5: Double Insertion – Master Challenge!",
            description: selLanguage
                ? "Εισήγαγε πρώτα τον E στην αρχή, μετά τον F μετά τον C. Τέλος, τοποθέτησε τον G στ τέλος της λίστας."
                : "Insert first E at the beginning, then insert F after C. Finally, place G at the end of the list.",
            initialList: [
                { id: "B", value: "20", next: "C", prev: null },
                { id: "C", value: "30", next: "D", prev: "B" },
                { id: "D", value: "40", next: null, prev: "C" }
            ],
            headId: "B",
            tailId: "D",
            newNodes: [
                { id: "E", value: "15", insertAtStart: true },
                { id: "F", value: "35", insertAfter: "C" },
                { id: "G", value: "50", insertAtEnd: true }
            ],
            expectedHead: "E",
            expectedTail: "D",
            expectedList: [
                { id: "E", value: "15", next: "B", prev: null },
                { id: "B", value: "20", next: "C", prev: "E" },
                { id: "C", value: "30", next: "F", prev: "B" },
                { id: "F", value: "35", next: "D", prev: "C" },
                { id: "D", value: "40", next: "G", prev: "F" },
                { id: "G", value: "50", next: null, prev: "D" }
            ]
        }
    ];


    useEffect(() => {
        loadLevel(currentLevel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLevel]);

    const loadLevel = (levelIndex) => {
        const level = LEVELS[levelIndex];
        setNodes(JSON.parse(JSON.stringify(level.initialList || [])));
        setHeadId(level.headId ?? null);
        setTailId(level.tailId ?? null);

        if (level.newNodes) {
            setPendingNodes(level.newNodes.map(n => ({ ...n, next: null, prev: null })));
        } else if (level.newNode) {
            setPendingNodes([{ ...level.newNode, next: null, prev: null }]);
        } else {
            setPendingNodes([]);
        }

        setShowSuccess(false);
        setShowError(false);
        setErrorMessage("");
        setActionMode(null);
        setReconnectFrom(null);
        setReconnectDirection(null);
        setShowGameOver(false);
        // reset removed edges for fresh level
        setRemovedEdges(new Set());
    };

    const resetGame = () => {
        setLives(3);
        setCurrentLevel(0)
        setShowGameOver(false);
        setShowGameWon(false);
        setActionMode(null)
    }

    const resetLevel = () => {
        loadLevel(currentLevel);
    };

    const handleMoveHead = (nodeId) => {
        setHeadId(nodeId);
    };

    const handleMoveTail = (nodeId) => {
        setTailId(nodeId);

    };

    const handleInsertNode = (pendingNodeId) => {
        const pendingNode = pendingNodes.find(n => n.id === pendingNodeId);
        if (!pendingNode) return;

        setNodes(prev => [...prev, { ...pendingNode }]);
        setPendingNodes(prev => prev.filter(n => n.id !== pendingNodeId));
    };


    const handleDisconnect = (nodeId, direction) => {
        const nodeToDisconnect = nodes.find(n => n.id === nodeId);

        if (!nodeToDisconnect || nodeToDisconnect[direction] === null) {
            setErrorMessage(selLanguage ? `Ο κόμβος ${nodeId} δεν έχει σύνδεση ${direction}!` : `Node ${nodeId} has no ${direction} connection!`);
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);

            return;
        }

        const targetId = nodeToDisconnect[direction];

        // ΑΛΛΑΓΗ: Αφαίρεσε ΜΟΝΟ την συγκεκριμένη σύνδεση
        setNodes(prev => prev.map(node => {
            if (node.id === nodeId) return { ...node, [direction]: null };
            return node; // Δεν αλλάζουμε τον άλλο κόμβο
        }));

        // record removed edge
        setRemovedEdges(prev => {
            const copy = new Set(Array.from(prev));
            copy.add(`${nodeId}:${direction}`);
            return copy;
        });


    };

    const handleReconnect = (fromId, toId, direction) => {
        const fromNode = nodes.find(n => n.id === fromId);
        if (!fromNode) {
            setErrorMessage(selLanguage ? `Ο κόμβος ${fromId} δεν βρέθηκε!` : `Node ${fromId} not found!`);
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);
            setReconnectFrom(null);
            setReconnectDirection(null);

            return;
        }

        // If connecting to NULL
        if (toId === null) {
            if (fromNode[direction] !== null) {
                setErrorMessage(selLanguage ? `Ο κόμβος ${fromId} έχει ήδη σύνδεση ${direction}.` : `Node ${fromId} already has a ${direction} connection.`);
                setShowError(true);
                setTimeout(() => setShowError(false), 2000);
                setReconnectFrom(null);
                setReconnectDirection(null);
                return;
            }

            setNodes(prev => prev.map(n => n.id === fromId ? { ...n, [direction]: null } : n));
            setRemovedEdges(prev => {
                const copy = new Set(Array.from(prev));
                copy.add(`${fromId}:${direction}`);
                return copy;
            });

            if (direction === 'prev') setHeadId(fromId);
            if (direction === 'next') setTailId(fromId);

            setReconnectFrom(null);
            setReconnectDirection(null);
            return;
        }

        const toNode = nodes.find(n => n.id === toId);
        if (!toNode) {
            setErrorMessage(selLanguage ? `Ο στόχος ${toId} δεν βρέθηκε!` : `Target ${toId} not found!`);
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);
            setReconnectFrom(null);
            setReconnectDirection(null);
            return;
        }

        if (fromNode[direction] !== null) {
            setErrorMessage(selLanguage ? `Ο κόμβος ${fromId} έχει ήδη σύνδεση ${direction}.` : `Node ${fromId} already has a ${direction} connection.`);
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);
            setReconnectFrom(null);
            setReconnectDirection(null);
            return;
        }

        // ΑΛΛΑΓΗ: Ενημέρωσε ΜΟΝΟ τον fromNode
        setNodes(prev => prev.map(n => {
            if (n.id === fromId) return { ...n, [direction]: toId };
            return n; // Δεν αλλάζουμε τον toNode
        }));

        // remove any removedEdges marks
        setRemovedEdges(prev => {
            const copy = new Set(Array.from(prev));
            copy.delete(`${fromId}:${direction}`);
            return copy;
        });

        setReconnectFrom(null);
        setReconnectDirection(null);
    };

    const checkSolution = () => {
        const level = LEVELS[currentLevel];
        let newLives = lives;

        if (pendingNodes.length > 0) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? `Λάθος! Πρέπει να εισάγεις όλους τους κόμβους που περιμένουν!` : `Wrong! You must insert all pending nodes!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        if (headId !== level.expectedHead) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? `Λάθος! Προσπάθησε ξανά προσεκτικά!` : `Wrong! Try again carefully!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        if (tailId !== level.expectedTail) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? `Λάθος! Προσπάθησε ξανά προσεκτικά!` : `Wrong! Try again carefully!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        const currentIds = nodes.map(n => n.id).sort();
        const expectedIds = level.expectedList.map(n => n.id).sort();

        if (JSON.stringify(currentIds) !== JSON.stringify(expectedIds)) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? "Λάθος! Οι κόμβοι στη λίστα δεν είναι σωστοί" : "Wrong! The nodes in the list are not correct");
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        for (const node of nodes) {
            const expected = level.expectedList.find(n => n.id === node.id);
            if (node.next !== expected.next || node.prev !== expected.prev) {
                newLives = lives - 1;
                setErrorMessage(selLanguage ? `Λάθος! Οι συνδέσεις του κόμβου ${node.id} δεν είναι σωστές` : `Wrong! Connections of node ${node.id} are not correct`);
                setShowError(true);
                setLives(newLives);

                if (newLives === 0) {
                    setTimeout(() => setShowGameOver(true), 1000);
                } else {
                    setTimeout(() => setShowError(false), 2000);
                }
                return;
            }
        }

        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            if (currentLevel < LEVELS.length - 1) {
                setCurrentLevel(prev => prev + 1);
                //setLives(3);
            } else {
                setShowGameWon(true);
            }
        }, 2500);
    };

    const handleInsertBetween = (beforeId, afterId) => {
        if (pendingNodes.length === 0) return;

        // Έλεγχος ότι έχουν επιλεγεί και οι δύο κόμβοι
        if (beforeId === undefined || afterId === undefined) {
            setErrorMessage(selLanguage ? "Πρέπει να επιλέξεις και τους δύο κόμβους!" : "You must select both nodes!");
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);
            setInsertMode(null);
            setInsertBetweenNodes({ before: null, after: null });
            return;
        }

        const newNode = pendingNodes[0];

        // ΝΕΟ: Εξειδικευμένος έλεγχος για NULL identifiers
        let beforeNode = null;
        let afterNode = null;

        // Αν το beforeId είναι NULL-END ή afterId είναι NULL-START, swap them
        if (beforeId === 'NULL-END' && afterId !== 'NULL-START') {
            // Swap: Ο χρήστης πάτησε το NULL του τέλους πρώτα
            const temp = beforeId;
            beforeId = afterId;
            afterId = temp;
        } else if (afterId === 'NULL-START' && beforeId !== 'NULL-END') {
            // Swap: Ο χρήστης πάτησε το NULL της αρχής δεύτερο
            const temp = beforeId;
            beforeId = afterId;
            afterId = temp;
        }

        // Normalize: Μετατροπή NULL-START και NULL-END σε null
        if (beforeId === 'NULL-START' || beforeId === 'NULL-END') {
            beforeNode = null;
        } else if (beforeId !== null && beforeId !== 'NULL') {
            beforeNode = nodes.find(n => n.id === beforeId);
        }

        if (afterId === 'NULL-START' || afterId === 'NULL-END') {
            afterNode = null;
        } else if (afterId !== null && afterId !== 'NULL') {
            afterNode = nodes.find(n => n.id === afterId);
        }

        // Δημιούργησε τον νέο κόμβο με prev και next
        const updatedNewNode = {
            ...newNode,
            next: afterNode ? afterNode.id : null,
            prev: beforeNode ? beforeNode.id : null
        };

        // Ενημέρωσε τους γειτονικούς κόμβους
        let newNodesArr = nodes.map(n => {
            if (beforeNode && n.id === beforeNode.id) {
                return { ...n, next: newNode.id };
            }
            if (afterNode && n.id === afterNode.id) {
                return { ...n, prev: newNode.id };
            }
            return n;
        });

        // Βρες τη σωστή θέση για εισαγωγή
        if (afterNode) {
            const afterIndex = newNodesArr.findIndex(n => n.id === afterNode.id);
            newNodesArr.splice(afterIndex, 0, updatedNewNode);
        } else if (beforeNode) {
            const beforeIndex = newNodesArr.findIndex(n => n.id === beforeNode.id);
            newNodesArr.splice(beforeIndex + 1, 0, updatedNewNode);
        } else {
            // Άδεια λίστα
            newNodesArr = [updatedNewNode];
            setHeadId(newNode.id);
            setTailId(newNode.id);
        }

        setNodes(newNodesArr);
        setPendingNodes(prev => prev.filter(n => n.id !== newNode.id));
        setActionMode(null);
        setInsertMode(null);
        setInsertBetweenNodes({ before: null, after: null });
    };

    const isNullId = (id) => id === 'NULL' || id === 'NULL-START' || id === 'NULL-END';

    const handleNodeClick = (nodeId, isPending = false) => {
        setSelectedNodeId(nodeId);
        const treatAsNull = isNullId(nodeId);

        if (actionMode === 'move-head' && !isPending && !treatAsNull) {
            handleMoveHead(nodeId);
            return;
        } else if (actionMode === 'move-tail' && !isPending && !treatAsNull) {
            handleMoveTail(nodeId);
            return;
        } else if (actionMode === 'insert-node' && !isPending) {
            if (insertMode === 'selecting-before') {
                // ΝΕΟ: Διατήρησε το συγκεκριμένο NULL identifier
                const beforeValue = treatAsNull ? nodeId : nodeId; // Κρατάμε 'NULL-START' ή 'NULL-END'
                setInsertBetweenNodes({ before: beforeValue, after: null });
                setInsertMode('selecting-after');
                return;
            } else if (insertMode === 'selecting-after') {
                // ΝΕΟ: Διατήρησε το συγκεκριμένο NULL identifier
                const afterValue = treatAsNull ? nodeId : nodeId; // Κρατάμε 'NULL-START' ή 'NULL-END'
                handleInsertBetween(insertBetweenNodes.before, afterValue);
                return;
            }
        } else if (actionMode === 'disconnect-next' && !isPending && !treatAsNull) {
            handleDisconnect(nodeId, 'next');
            return;
        } else if (actionMode === 'disconnect-prev' && !isPending && !treatAsNull) {
            handleDisconnect(nodeId, 'prev');
            return;
        } else if ((actionMode === 'reconnect-next' || actionMode === 'reconnect-prev') && !isPending) {
            const dir = actionMode === 'reconnect-next' ? 'next' : 'prev';
            if (!reconnectFrom && !treatAsNull) {
                setReconnectFrom(nodeId);
                setReconnectDirection(dir);
                return;
            } else if (reconnectFrom) {
                const targetId = treatAsNull ? null : nodeId;
                handleReconnect(reconnectFrom, targetId, reconnectDirection);
                return;
            }
        }
    };

    const SVG_WIDTH = 1400; // ίδιο με το viewBox

    const getNodePosition = (index) => {
        const containerWidth = 1400;      // ίσο με το viewBox width
        const baseSpacing = 200;          // βασική απόσταση μεταξύ κόμβων
        const padding = 80;               // κενό αριστερά/δεξιά

        const totalSlots = nodes.length + 1; // κόμβοι + NULL στο τέλος

        // πόσο πλάτος θα χρειαζόταν αν κρατήσουμε το baseSpacing
        const requiredWidth = (totalSlots - 1) * baseSpacing;

        // αν ΔΕΝ χωράμε, μικραίνουμε spacing. Αλλιώς scale = 1.
        const maxAvailableWidth = containerWidth - 2 * padding;
        const scaleFactor =
            requiredWidth > maxAvailableWidth
                ? maxAvailableWidth / requiredWidth
                : 1;

        const spacing = baseSpacing * scaleFactor;
        const totalWidth = (totalSlots - 1) * spacing;

        // κεντράρισμα όλου του “τρένου” στη μέση
        const startX = (containerWidth - totalWidth) / 2;

        return {
            x: startX + index * spacing,
            y: 225,
            scale: scaleFactor,
        };
    };




    const level = LEVELS[currentLevel];

    return (
        <div
            className="insertDoublyContainer main-content"
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >


            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}

            />
            {/* Header */}
            <div style={{
                width: '100%',
                maxWidth: '900px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div style={{
                    color: darkMode ? "white" : "#1e293b",
                    padding: "12px 24px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "18px",
                    border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                }}>

                    {selLanguage ? `Επίπεδο ${level.id}/${LEVELS.length}` : `Level ${level.id}/${LEVELS.length}`}

                </div>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "right",
                    gap: "12px"
                }}>


                    <div style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center"
                    }}>
                        {[1, 2, 3].map((heartNum) => (
                            <div
                                key={heartNum}
                                style={{
                                    fontSize: "24px",
                                    filter: lives >= heartNum
                                        ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                        : "grayscale(100%) opacity(0.3)",
                                    transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                ❤️
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="insertDoublyProm">
                <p style={{ color: '#ffffffff', marginBottom: '15px', lineHeight: '1.6', fontSize: '15px' }}>{level.description}</p>
            </div>

            {/* Pending Nodes */}
            {
                pendingNodes.length > 0 && (
                    <div style={{
                        background: '#dbeafe',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #3b82f6',
                        width: '90%',
                        maxWidth: '870px'
                    }}>
                        <h3 style={{ color: '#1e40af', marginBottom: '15px' }}> {selLanguage ? "Κόμβοι προς Εισαγωγή" : "Nodes to Insert"}
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {pendingNodes.map((node, index) => (
                                <motion.div
                                    key={node.id}
                                    style={{
                                        background: '#3b82f6',
                                        color: 'white',
                                        padding: '15px 25px',
                                        borderRadius: '12px',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        border: '3px solid white',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }}
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: index * 0.1 }}


                                >
                                    {node.id} = {node.value}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* Action Buttons */}
            <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                maxWidth: '900px'
            }}>
                <button
                    onClick={() => {
                        if (pendingNodes.length === 0) {
                            setErrorMessage(selLanguage ? "Δεν υπάρχουν κόμβοι προς εισαγωγή!" : "No nodes to insert!");
                            setShowError(true);
                            setTimeout(() => setShowError(false), 2000);
                            return;
                        }
                        setActionMode('insert-node');
                        setInsertMode('selecting-before');
                        setInsertBetweenNodes({ before: null, after: null });
                    }}
                    className={`doublyInsertButton ${actionMode === 'insert-node' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Προσθήκη κόμβου" : "Insert Node"}
                </button>
                <button
                    onClick={() => setActionMode(actionMode === 'move-head' ? null : 'move-head')}
                    className={`doublyInsertButton ${actionMode === 'move-head' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Μετακίνηση HEAD" : "Move HEAD"}
                </button>
                <button
                    onClick={() => setActionMode(actionMode === 'move-tail' ? null : 'move-tail')}
                    className={`doublyInsertButton ${actionMode === 'move-tail' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Μετακίνηση TAIL" : "Move TAIL"}
                </button>
                <button
                    onClick={() => setActionMode(actionMode === "disconnect-next" ? null : "disconnect-next")}
                    className={`doublyInsertButton ${actionMode === 'disconnect-next' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Αφαίρεση Next" : "Disconnect Next"}
                </button>
                <button
                    onClick={() => setActionMode(actionMode === "disconnect-prev" ? null : "disconnect-prev")}
                    className={`doublyInsertButton ${actionMode === 'disconnect-prev' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Αφαίρεση Prev" : "Disconnect Prev"}
                </button>
                <button
                    onClick={() => {
                        setActionMode(actionMode === 'reconnect-next' ? null : 'reconnect-next');
                        setReconnectFrom(null);
                    }}
                    className={`doublyInsertButton ${actionMode === 'reconnect-next' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Σύνδεση Next" : "Reconnect Next"}
                </button>
                <button
                    onClick={() => {
                        setActionMode(actionMode === 'reconnect-prev' ? null : 'reconnect-prev');
                        setReconnectFrom(null);
                    }}
                    className={`doublyInsertButton ${actionMode === 'reconnect-prev' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Σύνδεση Prev" : "Reconnect Prev"}
                </button>
            </div>

            {
                reconnectFrom && (
                    <div style={{
                        background: '#fed7aa',
                        padding: '12px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        marginBottom: '20px',
                        border: '2px solid #f59e0b',
                        maxWidth: '600px'
                    }}>
                        {
                            selLanguage
                                ? `Επέλεξε τον κόμβο στον οποίο θέλεις να συνδέσεις το ${reconnectFrom} (${reconnectDirection === 'next' ? 'Επόμενο' : 'Προηγούμενο'}) ή NULL`
                                : `Select the node you want to connect ${reconnectFrom} (${reconnectDirection === 'next' ? 'Next' : 'Prev'}) or NULL`
                        }
                    </div>
                )
            }

            {/* Linked List Visualization */}
            <div className="insertDoublyVisual">
                <svg width="100%" height="500" viewBox={`0 0 ${SVG_WIDTH} 500`}>
                    <defs>
                        <marker
                            id="arrowhead-next"
                            markerWidth="10"
                            markerHeight="10"
                            refX="9"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="0 0, 10 3, 0 6" fill="#3b82f6" />
                        </marker>
                        <marker
                            id="arrowhead-prev"
                            markerWidth="10"
                            markerHeight="10"
                            refX="10"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="10 3, 0 0, 0 6" fill="#f59e0b" />
                        </marker>
                    </defs>

                    {/* Draw NEXT connections (top arrows) */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        if (node.next) {
                            const nextNode = nodes.find(n => n.id === node.next);
                            if (nextNode) {
                                const nextIndex = nodes.indexOf(nextNode);
                                const nextPos = getNodePosition(nextIndex);

                                return (
                                    <motion.line
                                        key={`next-${node.id}`}
                                        x1={pos.x + 65}
                                        y1={pos.y - 20}
                                        x2={nextPos.x + 5}
                                        y2={nextPos.y - 18}
                                        stroke="#10b981"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead-next)"
                                        initial={{ pathLength: 0 }
                                        }
                                        animate={{ pathLength: 1 }
                                        }
                                        transition={{ duration: 0.5 }}
                                    />

                                );
                            }
                        } else {
                            // do not render dashed NULL arrow if this edge was explicitly removed
                            if (removedEdges.has(`${node.id}:next`)) return null;
                            const nullPos = getNodePosition(nodes.length);
                            return (
                                <motion.path
                                    key={`next-${node.id}-null`}
                                    d={`M ${pos.x + 65} ${pos.y - 20} Q ${(pos.x + nullPos.x) / 2} ${pos.y - 50} ${nullPos.x - 5} ${nullPos.y}`}
                                    stroke="#10b981"
                                    strokeWidth="3"
                                    fill="none"
                                    strokeDasharray="5,5"
                                    markerEnd="url(#arrowhead-next)"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Draw PREV connections (bottom) */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        if (node.prev) {
                            const prevNode = nodes.find(n => n.id === node.prev);
                            if (prevNode) {
                                const prevIndex = nodes.indexOf(prevNode);
                                const prevPos = getNodePosition(prevIndex);

                                return (
                                    <motion.line
                                        key={`prev-${node.id}`}
                                        x1={pos.x + 10}
                                        y1={pos.y + 25}
                                        x2={prevPos.x + 70}
                                        y2={prevPos.y + 25}
                                        stroke="#f59e0b"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead-prev)"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5 }}
                                    />

                                );
                            }
                        } else {
                            // do not render dashed NULL prev arrow if this edge was explicitly removed
                            if (removedEdges.has(`${node.id}:prev`)) return null;
                            const nullPrevPos = { x: getNodePosition(0).x - 120, y: getNodePosition(0).y };
                            return (
                                <motion.line
                                    key={`prev-${node.id}-null`}
                                    x1={pos.x + 10}
                                    y1={pos.y}
                                    x2={nullPrevPos.x + 45}
                                    y2={nullPrevPos.y}
                                    stroke="#f59e0b"
                                    strokeWidth="3"
                                    strokeDasharray="5,5"
                                    markerEnd="url(#arrowhead-prev)"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Draw HEAD indicator */}
                    {headId && nodes.find(n => n.id === headId) && (
                        <motion.g
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                        >
                            <text
                                x={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 35}
                                y={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 70}
                                textAnchor="middle"
                                fill="#10b981"
                                fontSize="22"
                                fontWeight="700"
                            >
                                HEAD
                            </text>
                            <line
                                x1={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 35}
                                y1={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 55}
                                x2={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 35}
                                y2={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 40}
                                stroke="#10b981"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead-next)"
                            />
                        </motion.g>
                    )}

                    {/* Draw TAIL indicator */}
                    {tailId && nodes.find(n => n.id === tailId) && (
                        <motion.g
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                        >
                            <text
                                x={getNodePosition(nodes.findIndex(n => n.id === tailId)).x + 35}
                                y={getNodePosition(nodes.findIndex(n => n.id === tailId)).y + 80}
                                textAnchor="middle"
                                fill="#8b5cf6"
                                fontSize="22"
                                fontWeight="700"
                            >
                                TAIL
                            </text>
                            <line
                                x1={getNodePosition(nodes.findIndex(n => n.id === tailId)).x + 35}
                                y1={getNodePosition(nodes.findIndex(n => n.id === tailId)).y + 40}
                                x2={getNodePosition(nodes.findIndex(n => n.id === tailId)).x + 35}
                                y2={getNodePosition(nodes.findIndex(n => n.id === tailId)).y + 60}
                                stroke="#8b5cf6"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead-next)"
                            />
                        </motion.g>
                    )}

                    {/* Draw nodes */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        return (
                            <motion.g
                                key={node.id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <rect
                                    x={pos.x}
                                    y={pos.y - 35}
                                    width="70"
                                    height="70"
                                    fill={selectedNodeId === node.id ? "#fde68a" : "#eff6ff"}   // κιτρινωπή επισήμανση
                                    stroke={selectedNodeId === node.id ? "#f59e0b" : "#3b82f6"} // πορτοκαλί περίγραμμα
                                    strokeWidth="4"
                                    rx="10"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleNodeClick(node.id, false)}
                                />

                                <text
                                    x={pos.x + 35}
                                    y={pos.y - 10}
                                    textAnchor="middle"
                                    fill="#1e293b"
                                    fontSize="18"
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.id}
                                </text>
                                <text
                                    x={pos.x + 35}
                                    y={pos.y + 12}
                                    textAnchor="middle"
                                    fill="#64748b"
                                    fontSize="14"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.value}
                                </text>

                                {/* Next pointer indicator */}
                                <circle
                                    cx={pos.x + 65}
                                    cy={pos.y - 25}
                                    r="6"
                                    fill="#3b82f6"
                                />
                                <text
                                    x={pos.x + 65}
                                    y={pos.y - 38}
                                    textAnchor="middle"
                                    fill="#3b82f6"
                                    fontSize="10"
                                    fontWeight="600"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    N
                                </text>

                                {/* Prev pointer indicator */}
                                <circle
                                    cx={pos.x + 5}
                                    cy={pos.y + 25}
                                    r="6"
                                    fill="#f97316"
                                />
                                <text
                                    x={pos.x + 5}
                                    y={pos.y + 42}
                                    textAnchor="middle"
                                    fill="#f97316"
                                    fontSize="10"
                                    fontWeight="600"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    P
                                </text>
                            </motion.g>
                        );
                    })}

                    {/* NULL box (end) */}
                    {nodes.length > 0 && (
                        <g>
                            <rect
                                x={getNodePosition(nodes.length).x}
                                y={getNodePosition(nodes.length).y - 20}
                                width="60"
                                height="40"
                                fill="#1f2937"
                                stroke="#64748b"
                                strokeWidth="3"

                                rx="8"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    if (actionMode === 'reconnect-next' || actionMode === 'reconnect-prev') {
                                        if (reconnectFrom) handleNodeClick('NULL', false);
                                    } else if (actionMode === 'insert-node') {
                                        handleNodeClick('NULL-END', false);
                                    }
                                }}
                            />
                            <text
                                x={getNodePosition(nodes.length).x + 30}
                                y={getNodePosition(nodes.length).y + 5}
                                textAnchor="middle"
                                fill="#94a3b8"
                                fontSize="16"
                                cursor="pointer"
                                fontWeight="700"
                                style={{ pointerEvents: 'none' }}
                            >
                                NULL
                            </text>
                        </g>
                    )}

                    {/* NULL box at the start (for prev connections / insert at start) */}
                    {nodes.length > 0 && (() => {
                        const firstPos = getNodePosition(0);
                        const boxWidth = 50 * firstPos.scale;
                        const boxHeight = 30 * firstPos.scale;
                        const boxX = Math.max(10, firstPos.x - 80 * firstPos.scale);

                        return (
                            <g>
                                <rect
                                    x={boxX}
                                    y={firstPos.y - boxHeight / 2}
                                    width={boxWidth}
                                    height={boxHeight}
                                    fill="#1f2937"
                                    stroke="#64748b"
                                    strokeWidth={2 * firstPos.scale}
                                    rx={5 * firstPos.scale}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        if (actionMode === 'reconnect-prev' && reconnectFrom) handleNodeClick('NULL', false);
                                        else if (actionMode === 'insert-node') handleNodeClick('NULL-START', false);
                                    }}
                                />
                                <text
                                    x={boxX + boxWidth / 2}
                                    y={firstPos.y + 5 * firstPos.scale}
                                    textAnchor="middle"
                                    fill="#94a3b8"
                                    fontSize={14 * firstPos.scale}
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    NULL
                                </text>
                            </g>
                        );
                    })()}
                </svg>
            </div>

            {showError && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#fee2e2',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #dc2626',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >

                    <p style={{ color: '#991b1b', margin: 0 }}>
                        {errorMessage}
                    </p>
                </motion.div>
            )}

            {/* Success Message */}
            {showSuccess && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#d1fae5',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #10b981',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >
                    <p style={{ color: '#065f46', margin: 0 }}>
                        {selLanguage ? "Συγχαρητήρια! Προχωράς στο επόμενο επίπεδο!" : "Congratulations! You advance to the next level!"}
                    </p>
                </motion.div>
            )}

            {/* Control Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                    onClick={checkSolution}
                    className="doublyInsertionCheckButton"
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                    {selLanguage ? "Έλεγχος Λύσης" : "Check Solution"}
                </button>
                <button
                    onClick={resetLevel}
                    className="doublyInsertionResetButton"
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                    {selLanguage ? "Επαναφορά" : "Reset"}
                </button>
            </div>

        </div >
    );
};

export default DoublyLinkedListInsertionGame;